import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const jwtSchema = z
  .string()
  .min(1)
  .regex(/^[\w-]+\.[\w-]+\.[\w-]+$/, "Invalid JWT format");

// ── Payload Types ─────────────────────────────────────────
export interface AttachmentEntry {
  id: string;
  task_id: string | null;
  user_id: string;
  file_name: string;
  file_path: string;
  file_type: string | null;
  file_size: number;
  display_name: string | null;
  created_at: string;
}

export interface AttachmentStatsPayload {
  totalAttachments: number;
  totalStorageBytes: number;
  mostCommonType: string;
}

export interface AttachmentListPayload {
  attachments: AttachmentEntry[];
  total: number;
}

// ── Server Functions ──────────────────────────────────────
export const getAttachmentStats = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
    })
  )
  .handler(async ({ data }) => {
    const { createAdminClient } = await import("./_admin-utils.server");
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError ?? "Missing server env vars" };

    const { data: { user }, error: authError } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { data: null, error: "Unauthorized" };

    const { data: attachments, error, count } = await client
      .from("task_attachments")
      .select("file_type, file_size", { count: "exact" });

    if (error) return { data: null, error: error.message };

    const list = (attachments ?? []) as Array<{ file_type: string | null; file_size: number }>;
    const totalStorageBytes = list.reduce((sum, a) => sum + (Number(a.file_size) || 0), 0);

    const typeCounts: Record<string, number> = {};
    for (const a of list) {
      const t = a.file_type ?? "unknown";
      typeCounts[t] = (typeCounts[t] ?? 0) + 1;
    }
    const mostCommonType = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "none";

    const payload: AttachmentStatsPayload = {
      totalAttachments: count ?? 0,
      totalStorageBytes,
      mostCommonType,
    };

    return { data: payload, error: null };
  });

export const getAttachments = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      page: z.number().int().min(1).default(1),
      limit: z.number().int().min(1).max(100).default(20),
      search: z.string().optional(),
    })
  )
  .handler(async ({ data }) => {
    const { createAdminClient } = await import("./_admin-utils.server");
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError ?? "Missing server env vars" };

    const { data: { user }, error: authError } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { data: null, error: "Unauthorized" };

    const offset = (data.page - 1) * data.limit;

    let query = client
      .from("task_attachments")
      .select("id, task_id, user_id, file_name, file_path, file_type, file_size, display_name, created_at", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(offset, offset + data.limit - 1);

    if (data.search) {
      query = query.ilike("file_name", `%${data.search}%`);
    }

    const { data: attachments, error, count } = await query;

    if (error) return { data: null, error: error.message };

    const entries: AttachmentEntry[] = (attachments ?? []).map((a) => ({
      id: String(a.id),
      task_id: a.task_id ? String(a.task_id) : null,
      user_id: String(a.user_id),
      file_name: String(a.file_name),
      file_path: String(a.file_path),
      file_type: a.file_type ? String(a.file_type) : null,
      file_size: Number(a.file_size) || 0,
      display_name: a.display_name ? String(a.display_name) : null,
      created_at: String(a.created_at),
    }));

    return { data: { attachments: entries, total: count ?? 0 }, error: null };
  });
